/**
 * ResultView Component
 * Shows the result of a successful card scan
 */
import { useState } from 'react';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CircleCheck, DatabaseBackup, ArrowDown, ArrowUp, Info, RefreshCw, CircleDollarSign, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { auth, addCardToCollection } from '@/lib/firebase';
import { PokemonCard } from '@/lib/api';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ResultViewProps {
  card: PokemonCard;
  onScanAgain: () => void;
  onSaveToCollection: () => void;
}

export function ResultView({ card, onScanAgain, onSaveToCollection }: ResultViewProps) {
  const [priceTab, setPriceTab] = useState<'market' | 'trends'>('market');
  const [isSaving, setIsSaving] = useState(false);
  const isLoggedIn = !!auth.currentUser;
  const { toast } = useToast();
  
  // Updated to match CardDetail.tsx price calculation logic for consistency
  const getMainPrice = (): { value: number | null; currency: string } => {
    // First try TCGPlayer prices 
    if (card.tcgplayer?.prices) {
      // Get the first price object (normal, holofoil, etc)
      const firstPriceType = Object.keys(card.tcgplayer.prices)[0];
      if (firstPriceType && card.tcgplayer.prices[firstPriceType]?.market) {
        return { 
          value: card.tcgplayer.prices[firstPriceType].market,
          currency: '$' 
        };
      }
    }
    
    // Then fall back to Cardmarket prices
    if (card.cardmarket?.prices?.averageSellPrice) {
      return { 
        value: card.cardmarket.prices.averageSellPrice,
        currency: '€' 
      };
    }
    
    // Default if no price found
    return { value: null, currency: '$' };
  };
  
  // Format price for display
  const formatPrice = (price: number | undefined | null, currency: string): string => {
    if (price === undefined || price === null) return 'N/A';
    return `${currency}${price.toFixed(2)}`;
  };
  
  // Get price class based on value
  const getPriceClass = (price: number | null): string => {
    if (price === null) return 'text-gray-600';
    if (price >= 100) return 'text-rose-600';
    if (price >= 50) return 'text-purple-600';
    if (price >= 20) return 'text-blue-600';
    if (price >= 5) return 'text-green-600';
    return 'text-gray-600';
  };
  
  // FIXED: Get trend indicator (up, down, or neutral) with corrected calculation
  const getTrend = (): { direction: 'up' | 'down' | 'neutral', percent: number } => {
    if (card.cardmarket?.prices) {
      const avg1 = card.cardmarket.prices.avg1 || 0;
      const avg7 = card.cardmarket.prices.avg7 || 0;
      
      // Skip calculation if either value is 0 or missing
      if (avg1 <= 0 || avg7 <= 0) {
        return { direction: 'neutral', percent: 0 };
      }
      
      if (avg1 > avg7) {
        // Price is going up (avg1 > avg7)
        const percent = ((avg1 - avg7) / avg7) * 100;
        return { direction: 'up', percent };
      } else if (avg7 > avg1) {
        // Price is going down (avg7 > avg1)
        // For downward trends, need to show as negative percentage
        const percent = ((avg7 - avg1) / avg7) * 100;
        return { direction: 'down', percent };
      }
    }
    
    return { direction: 'neutral', percent: 0 };
  };
  
  const handleSaveToCollection = async () => {
    if (!isLoggedIn) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to save cards to your collection.",
      });
      return;
    }

    try {
      setIsSaving(true);
      
      // Prepare the card data to be stored (simplified version)
      const cardDataForCollection = {
        name: card.name,
        imageUrl: card.images.small,
        set: card.set.name,
        number: card.number,
        rarity: card.rarity || "Unknown",
        price: getMainPrice().value || 0,
        setTotal: card.set.printedTotal,
        id: card.id
      };
      
      const success = await addCardToCollection(card.id, cardDataForCollection);
      
      if (success) {
        onSaveToCollection();
        toast({
          title: "Success",
          description: "Card added to your collection!",
        });
      }
    } catch (error) {
      console.error("Error saving card:", error);
      toast({
        title: "Error",
        description: "Failed to add card to collection. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  const trend = getTrend();
  const mainPrice = getMainPrice();
  
  return (
    <motion.div
      className="w-full max-w-xl mx-auto"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card className="overflow-hidden">
        <div className="bg-green-600 text-white p-4">
          <h2 className="text-xl font-semibold flex items-center justify-center">
            <CircleCheck className="mr-2 h-6 w-6" />
            Card Identified
          </h2>
        </div>
        
        <CardContent className="p-6">
          {/* Card image and basic info */}
          <div className="flex flex-col sm:flex-row gap-4 items-center sm:items-start">
            {/* Card image */}
            <div className="w-40 h-56 rounded-lg overflow-hidden shadow-lg bg-white">
              <img 
                src={card.images.small} 
                alt={card.name} 
                className="w-full h-full object-contain"
              />
            </div>
            
            {/* Card details */}
            <div className="flex-grow space-y-3">
              <div>
                <h3 className="text-xl font-bold">{card.name}</h3>
                <p className="text-gray-500 text-sm">
                  {card.set.name} · {card.number}/{card.set.printedTotal}
                  {card.rarity && ` · ${card.rarity}`}
                </p>
              </div>
              
              {/* Price display - FIXED for clarity */}
              <div className="mt-3">
                <div className="mb-1 text-sm text-gray-500 flex items-center">
                  <CircleDollarSign className="h-4 w-4 mr-1" />
                  Market Value
                </div>
                <div className="flex items-end gap-2">
                  <span className={`text-2xl font-bold ${getPriceClass(mainPrice.value)}`}>
                    {formatPrice(mainPrice.value, mainPrice.currency)}
                  </span>
                  
                  {/* Price trend indicator - FIXED arrow direction */}
                  {trend.direction !== 'neutral' && trend.percent > 0 && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className={`flex items-center text-sm ${
                          trend.direction === 'up' ? 'text-green-500' : 'text-red-500'
                        }`}>
                          {trend.direction === 'up' ? (
                            <ArrowUp className="h-3 w-3 mr-1" />
                          ) : (
                            <ArrowDown className="h-3 w-3 mr-1" />
                          )}
                          <span>{trend.percent.toFixed(1)}%</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">
                          {trend.direction === 'up' 
                            ? 'Price trending up over the last 7 days' 
                            : 'Price trending down over the last 7 days'}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </div>
              
              {/* Set information */}
              <div className="flex items-center">
                {card.set.images?.symbol && (
                  <img 
                    src={card.set.images.symbol}
                    alt={card.set.name}
                    className="h-6 w-6 mr-2"
                  />
                )}
                <div className="text-sm text-gray-600">
                  Released: {new Date(card.set.releaseDate).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
          
          <Separator className="my-4" />
          
          {/* Price details tabs */}
          <div className="mt-4">
            <div className="flex border-b">
              <button
                className={`px-4 py-2 text-sm font-medium ${
                  priceTab === 'market' ? 'text-pokemon-blue border-b-2 border-pokemon-blue' : 'text-gray-500'
                }`}
                onClick={() => setPriceTab('market')}
              >
                Market Price
              </button>
              <button
                className={`px-4 py-2 text-sm font-medium ${
                  priceTab === 'trends' ? 'text-pokemon-blue border-b-2 border-pokemon-blue' : 'text-gray-500'
                }`}
                onClick={() => setPriceTab('trends')}
              >
                Trends
              </button>
            </div>
            
            <div className="py-3">
              {priceTab === 'market' && (
                <div className="space-y-2">
                  {/* Market Price Tab Content */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-gray-50 p-3 rounded">
                      <div className="text-xs text-gray-500">Average Price</div>
                      <div className="font-medium">
                        {card.cardmarket?.prices?.averageSellPrice 
                          ? `€${card.cardmarket.prices.averageSellPrice.toFixed(2)}` 
                          : 'N/A'}
                      </div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <div className="text-xs text-gray-500">Low Price</div>
                      <div className="font-medium">
                        {card.cardmarket?.prices?.lowPrice 
                          ? `€${card.cardmarket.prices.lowPrice.toFixed(2)}` 
                          : 'N/A'}
                      </div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <div className="text-xs text-gray-500">Trend Price</div>
                      <div className="font-medium">
                        {card.cardmarket?.prices?.trendPrice 
                          ? `€${card.cardmarket.prices.trendPrice.toFixed(2)}` 
                          : 'N/A'}
                      </div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <div className="text-xs text-gray-500">Updated</div>
                      <div className="font-medium">
                        {card.cardmarket?.updatedAt 
                          ? new Date(card.cardmarket.updatedAt).toLocaleDateString() 
                          : 'N/A'}
                      </div>
                    </div>
                  </div>

                  {/* New - Value Information */}
                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 mt-3">
                    <div className="flex items-start">
                      <div className="bg-blue-100 rounded-full p-1 mr-2">
                        <TrendingUp className="h-4 w-4 text-blue-700" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-blue-800">Market Value Insights</h4>
                        <p className="text-xs text-blue-700 mt-1">
                          Prices are aggregated from thousands of marketplace listings and actual sales data.
                          Values may fluctuate based on card condition, special attributes, and market demand.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {priceTab === 'trends' && card.cardmarket?.prices && (
                <div className="space-y-3">
                  {/* Trends Tab Content */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <h4 className="text-sm font-medium mb-1">1 Day Average</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-gray-50 p-2 rounded">
                          <div className="text-xs text-gray-500">Standard</div>
                          <div className="text-sm">{formatPrice(card.cardmarket.prices.avg1, '€')}</div>
                        </div>
                        <div className="bg-gray-50 p-2 rounded">
                          <div className="text-xs text-gray-500">Reverse Holo</div>
                          <div className="text-sm">{formatPrice(card.cardmarket.prices.reverseHoloAvg1, '€')}</div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium mb-1">7 Day Average</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-gray-50 p-2 rounded">
                          <div className="text-xs text-gray-500">Standard</div>
                          <div className="text-sm">{formatPrice(card.cardmarket.prices.avg7, '€')}</div>
                        </div>
                        <div className="bg-gray-50 p-2 rounded">
                          <div className="text-xs text-gray-500">Reverse Holo</div>
                          <div className="text-sm">{formatPrice(card.cardmarket.prices.reverseHoloAvg7, '€')}</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <h4 className="text-sm font-medium mb-1">30 Day Average</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-gray-50 p-2 rounded">
                          <div className="text-xs text-gray-500">Standard</div>
                          <div className="text-sm">{formatPrice(card.cardmarket.prices.avg30, '€')}</div>
                        </div>
                        <div className="bg-gray-50 p-2 rounded">
                          <div className="text-xs text-gray-500">Reverse Holo</div>
                          <div className="text-sm">{formatPrice(card.cardmarket.prices.reverseHoloAvg30, '€')}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
        
        <CardFooter className="bg-gray-50 p-4 flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onScanAgain}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Scan Another Card
          </Button>
          
          {isLoggedIn ? (
            <Button
              className="flex-1"
              onClick={handleSaveToCollection}
              disabled={isSaving}
            >
              <DatabaseBackup className="mr-2 h-4 w-4" />
              {isSaving ? "Adding..." : "Add to Collection"}
            </Button>
          ) : (
            <Button
              className="flex-1"
              variant="secondary"
              asChild
            >
              <Link to="/profile">
                Sign in to Save
              </Link>
            </Button>
          )}
          
          <Button 
            variant="default"
            asChild
          >
            <Link to={`/card/${card.id}`}>
              View Details
            </Link>
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
}
